import { supabase } from '../supabase';

// The owner's coach billing screen (plan 014; migration 112). One read for the screen; each change is one of the
// database's own owner-only actions, which refuse anyone else.

/** Everything the screen shows. Throws if it could not be read. */
export async function loadOwnerCoachBilling({ oldestFirst = false } = {}) {
  const { data, error } = await supabase.rpc('owner_coach_billing', { p_oldest_first: oldestFirst });
  if (error) throw error;
  if (!data?.checks) throw new Error('Coach billing could not be read. Please try again.');
  return data;
}

/**
 * Makes an active coach with no plan a paying coach. A coach who already has clients is changing model: only with
 * exception (plan 014; Codex's review, PR 307). Returns { result: 'made'|'has_clients'|'already_has_a_plan'|'not_an_active_coach' }.
 */
export async function makePayingCoach(coachId, exception = false) {
  const { data, error } = await supabase.rpc('make_paying_coach', { p_coach: coachId, p_exception: exception });
  if (error) throw error;
  return data;
}

/** Marks a flag done. Returns true, or false if it had already been marked. */
export async function markFlagDone(flagId) {
  const { data, error } = await supabase.rpc('mark_owner_flag_done', { p_flag: flagId });
  if (error) throw error;
  return data === true;
}

/**
 * The coach payments switch (112). Switching off is refused while any coach's billing is live, because Stripe keeps
 * charging while the repair jobs stop (PR 307, P1). Returns { result: 'set' } or { result: 'billing_live', coaches }.
 */
export async function setPaymentsSwitch(on) {
  const { data, error } = await supabase.rpc('set_coach_payments_switch', { p_on: on });
  if (error) throw error;
  if (data?.result === 'set' && data.features_on !== on) throw new Error('The change was not saved. Please try again.');
  if (!data?.result) throw new Error('The change was not saved. Please try again.');
  return data;
}

/** The owner's coach account, for clients who move to the owner (093); the other settings are left as they are. */
export async function setOwnerCoach(ownerCoach) {
  const { data, error } = await supabase.rpc('set_coach_cover_settings',
    { p_open_slots: null, p_owner_coach: ownerCoach, p_features_on: null });
  if (error) throw error;
  if (data?.owner_coach_id !== ownerCoach) throw new Error('The change was not saved. Please try again.');
  return data;
}

// What each flag means, in a line, from where the database or server raises it (migrations 093 to 112 and
// api/lib; checked against each producer by Codex, PR 307). tone: 'act' when the owner should do something, 'note'
// when it is for information. Marking an 'act' flag done asks first: it fixes nothing, and the same problem does
// not flag again.
const FLAGS = {
  did_not_pay: ['act', 'A client\'s cover ended without a payment', 'As of when it ended; they may have paid since.'],
  plan_ended_seven_days: ['note', 'A coach\'s plan ended, so a covered client\'s cover is ending', 'They have until the date shown (up to 7 days) to pay and stay with you, or ask for another coach.'],
  coaching_ended_covered: ['note', 'Coaching ended for a covered client', 'They have until the date shown (up to 7 days) to choose what happens next.'],
  coaching_ended_now_yours: ['note', 'A client\'s coaching ended, and they are now yours', ''],
  paid_now_yours: ['note', 'A client whose coaching ended has paid, and is now yours', ''],
  owner_coach_not_set: ['act', 'A client could not be moved to you: your coach account is not set', 'Set it at the top of this page. The move then happens by itself if they still need it.'],
  plan_ended_handover_held: ['act', 'A client could not be moved to you after their coach\'s plan ended', 'Fix the reason shown, then mark this done: the next run moves them, or this comes back if the reason remains.'],
  plan_ended_now_yours: ['note', 'A client with no covered place is now yours, because their coach\'s plan ended', 'Any subscription of their own is unchanged.'],
  founding_coach_added: ['note', 'A founding coach was added', ''],
  founding_ai_limit: ['act', 'A founding coach\'s clients reached the AI safety limit', ''],
  founding_ai_overrun: ['act', 'A founding coach\'s AI use cost more than was reserved', ''],
  paying_coach_added: ['note', 'A coach was made a paying coach', 'Nothing is charged until they add a card and accept their first covered client.'],
  coach_full: ['act', 'A client could not join: their coach is at the safety limit', 'Their request was closed. Talk to the coach.'],
  coach_reached_50: ['note', 'A coach has reached 50 covered clients', ''],
  client_history_unknown: ['act', 'Stripe could not show whether a client has paid before', 'Their coach\'s Accept could not go ahead. Decide whether the coach pays for them.'],
  first_charge_unresolved: ['act', 'A coach\'s first payment needs checking by hand in Stripe', 'No client is let in until it is settled.'],
  old_attempt_paid: ['act', 'An older first-payment attempt was paid', 'Check it in Stripe, and refund if the coach was charged twice.'],
  paid_with_no_client: ['act', 'A coach\'s first payment went through, but no client was let in', 'The reason is shown. Check whether the coach can still take a client this month, or refund.'],
  coach_renewal_unpaid: ['act', 'A coach\'s renewal has not been paid', 'Declined, waiting for their bank, or unanswered. Their 7 days to pay have started; they keep their clients meanwhile.'],
  coach_repeated_miss: ['act', 'A coach has missed a payment twice in 3 months', 'Decide whether to keep them.'],
  coach_plan_ended_unpaid: ['act', 'A coach\'s plan ended: not paid within 7 days', 'Their clients go through the ordinary 7 days.'],
  coach_plan_ended_cancelled: ['note', 'A coach\'s plan ended: they chose to stop paying', 'Their clients go through the ordinary 7 days.'],
  coach_chose_to_leave: ['note', 'A coach chose to stop paying', 'Their plan ends at their renewal date, and they can change their mind until then.'],
  coach_left_unpaid: ['note', 'A coach who was behind on payment stopped, so their plan ended at once', 'Stripe is asked to cancel the unpaid bill.'],
  coach_stayed: ['note', 'A coach changed their mind and stays', ''],
  coach_invoice_differs: ['act', 'A coach\'s invoice is not what was intended', 'The client count, price, currency or amount differs. Check it in Stripe; refund an overcharge if needed.'],
  coach_old_subscription_paid: ['act', 'A coach paid on an old subscription', 'Check it in Stripe.'],
  coach_paid_after_end: ['act', 'A coach paid after their plan had ended', 'Decide whether to refund. Their plan stays ended.'],
  coach_renewal_unexpected: ['act', 'A coach was charged a renewal when none was expected', 'Check it in Stripe.'],
  coach_renewed_while_stopping: ['act', 'Stripe tried to renew a coach who was stopping', 'It may have been paid or have failed (the reason is shown). Check it in Stripe; refund if they were charged.'],
  coach_subscription_ended_early: ['act', 'A coach\'s Stripe subscription ended unexpectedly', 'Check it in Stripe.'],
  coach_payment_refunded: ['note', 'A coach payment was refunded', ''],
  coach_payment_disputed: ['act', 'A coach payment has a dispute with their bank', 'Its state is shown. If it is still open, respond in Stripe.'],
  coach_charge_without_invoice: ['act', 'A refund or dispute could not be matched to a coach invoice', 'Check it in Stripe.'],
  coach_quantity_problem: ['act', 'The client count could not be set in Stripe for a coach', 'The jobs keep trying; check Stripe if it lasts.'],
  coach_quantity_changed_outside: ['note', 'A coach\'s client count was changed in Stripe outside the app', 'It has been put back.'],
  coach_billing_job_failing: ['act', 'The billing job keeps failing for a coach', 'Pass the error shown (the first of the day) to your engineering assistant.'],
  coach_jobs_not_set_up: ['act', 'The billing jobs could not start', 'The vault address or secret is missing or not valid, or the database\'s job tools are missing. Ask your engineering assistant.'],
  coach_jobs_not_answering: ['act', 'The billing jobs are not answering', 'Ask your engineering assistant: the Vercel secret, the vault address or the server may be at fault.'],
};

/** { tone, title, text } for a flag; any kind not listed still shows, in its own words. */
export function flagWords(flag) {
  const known = FLAGS[flag.kind]
    || (flag.kind.startsWith('coach_billing_task_')
      ? ['act', 'A billing step in Stripe needs checking', `Step: ${flag.kind.slice('coach_billing_task_'.length).replace(/_/g, ' ')}.`]
      : ['note', flag.kind.replace(/_/g, ' '), '']);
  return { tone: known[0], title: known[1], text: known[2] };
}

// Detail worth showing: dates as dates, money as pounds; the Stripe ids listed here are left out
const HIDE = new Set(['subscription', 'invoice', 'charge', 'event', 'customer', 'dispute', 'task', 'payment_intent']);
export function flagDetail(detail) {
  if (!detail || typeof detail !== 'object') return [];
  return Object.entries(detail)
    .filter(([k, v]) => !HIDE.has(k) && v !== null && v !== undefined && typeof v !== 'object')
    .map(([k, v]) => {
      if (/_pence$/.test(k) && typeof v === 'number') return `${k.replace(/_pence$/, '').replace(/_/g, ' ')}: £${(v / 100).toFixed(2)}`;
      const text = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)
        ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
        : String(v).replace(/_/g, ' ');
      return `${k.replace(/_/g, ' ')}: ${text}`;
    });
}

/** Where a coach's plan stands, in a few words */
export function planWords(p) {
  if (p.status === 'ended' || p.access_ended) return 'Ended';
  if (p.band === 'founding') return 'Founding';
  if (p.band !== 'standard') return 'Custom';
  if (p.state === 'paid' && p.deadline) return 'Renewal unanswered';
  if (p.state === 'unpaid') {
    return { bank_approval: 'Waiting for bank approval', unanswered: 'Renewal unanswered' }[p.unpaid_reason] || 'Payment failed';
  }
  return {
    no_card: 'No card yet', ready: 'Card saved', charging: 'First payment going through', paid: 'Paid up',
    cancelling: 'Stopping at renewal', stopping: 'No clients: stops at renewal', paused: 'Paused',
  }[p.state] || p.state;
}
